/**
 * Modal for generating a new marker (creating a new box with an unused marker).
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { EditModal } from './EditModal';
import { useBoxStore } from '../store/BoxContext';
import { useToast } from '../store/ToastContext';
import {
  getAvailableArucoIds,
  getArucoSvgUrl,
  getQrSvgUrl,
  checkMarkerAvailable,
} from '../api/markers';
import type { MarkerType } from '@shared/types';

interface NewMarkerModalProps {
  onClose: () => void;
}

export function NewMarkerModal({ onClose }: NewMarkerModalProps) {
  const navigate = useNavigate();
  const { createBox } = useBoxStore();
  const { addToast } = useToast();
  const [markerType, setMarkerType] = useState<MarkerType>('aruco');
  const [markerId, setMarkerId] = useState('');
  const [boxName, setBoxName] = useState('');
  const [boxLocation, setBoxLocation] = useState('');
  const [boxNotes, setBoxNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [loadingAvailableIds, setLoadingAvailableIds] = useState(false);
  const [availableArucoIds, setAvailableArucoIds] = useState<number[]>([]);
  const [validating, setValidating] = useState(false);
  const [isAvailable, setIsAvailable] = useState<boolean | null>(null);

  // Load available ArUco IDs on mount
  useEffect(() => {
    async function loadAvailableIds() {
      setLoadingAvailableIds(true);
      try {
        const ids = await getAvailableArucoIds();
        setAvailableArucoIds(ids);
        if (ids.length > 0 && markerType === 'aruco') {
          setMarkerId(String(ids[0]));
        }
      } catch (err) {
        console.error('Failed to load available ArUco IDs:', err);
        addToast('error', 'Failed to load available markers');
      } finally {
        setLoadingAvailableIds(false);
      }
    }
    loadAvailableIds();
  }, [addToast, markerType]);

  // Reset markerId when switching marker type
  useEffect(() => {
    if (markerType === 'aruco' && availableArucoIds.length > 0) {
      setMarkerId(String(availableArucoIds[0]));
    } else if (markerType === 'qr') {
      setMarkerId('');
    }
  }, [markerType, availableArucoIds]);

  // Validate QR marker availability
  useEffect(() => {
    if (markerType !== 'qr' || !markerId.trim()) {
      setIsAvailable(null);
      return;
    }

    const timer = setTimeout(async () => {
      setValidating(true);
      try {
        const available = await checkMarkerAvailable(markerType, markerId);
        setIsAvailable(available);
      } catch (err) {
        console.error('Failed to check marker availability:', err);
        setIsAvailable(null);
      } finally {
        setValidating(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [markerType, markerId]);

  function getPreviewUrl(type: MarkerType, id: string, size = 128): string {
    if (type === 'aruco') {
      const numId = parseInt(id, 10);
      if (isNaN(numId) || numId < 0 || numId > 49) return '';
      return getArucoSvgUrl(numId, size);
    }
    return id.trim() ? getQrSvgUrl(id, size) : '';
  }

  async function handleCreate() {
    if (!boxName.trim()) {
      addToast('error', 'Box name is required');
      return;
    }

    if (!markerId.trim()) {
      addToast('error', 'Marker ID is required');
      return;
    }

    if (markerType === 'aruco') {
      const numId = parseInt(markerId, 10);
      if (isNaN(numId) || numId < 0 || numId > 49) {
        addToast('error', 'ArUco ID must be between 0 and 49');
        return;
      }
    }

    if (markerType === 'qr' && isAvailable === false) {
      addToast('error', 'This marker is already in use');
      return;
    }

    setSaving(true);
    try {
      const box = await createBox({
        markerType,
        markerId: markerId.trim(),
        name: boxName.trim(),
        location: boxLocation.trim() || undefined,
        notes: boxNotes.trim() || undefined,
      });
      addToast('success', 'Marker generated and box created');
      onClose();
      navigate(`/boxes/${box.id}`);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to create marker';
      addToast('error', errorMsg);
    } finally {
      setSaving(false);
    }
  }

  const canCreate =
    boxName.trim().length > 0 &&
    markerId.trim().length > 0 &&
    (markerType === 'aruco' || isAvailable === true) &&
    !validating &&
    !saving;

  return (
    <EditModal open={true} onClose={onClose} title="Generate New Marker">
      <div className="space-y-4">
        {/* Marker Type */}
        <div>
          <label className="mb-1 block text-sm font-medium text-text-primary">Marker Type</label>
          <select
            value={markerType}
            onChange={(e) => setMarkerType(e.target.value as MarkerType)}
            className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-text-primary focus:border-accent focus:outline-none"
          >
            <option value="aruco">ArUco (IDs 0-49)</option>
            <option value="qr">QR Code (custom ID)</option>
          </select>
        </div>

        {/* Marker ID */}
        <div>
          <label className="mb-1 block text-sm font-medium text-text-primary">Marker ID</label>
          {markerType === 'aruco' ? (
            <>
              {loadingAvailableIds ? (
                <p className="text-sm text-text-secondary">Loading available IDs...</p>
              ) : availableArucoIds.length === 0 ? (
                <p className="text-sm text-red-500">All ArUco markers (0-49) are in use</p>
              ) : (
                <select
                  value={markerId}
                  onChange={(e) => setMarkerId(e.target.value)}
                  className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-text-primary focus:border-accent focus:outline-none"
                >
                  {availableArucoIds.map((id) => (
                    <option key={id} value={String(id)}>
                      {id}
                    </option>
                  ))}
                </select>
              )}
              <p className="mt-1 text-xs text-text-secondary">
                {availableArucoIds.length} ArUco marker{availableArucoIds.length !== 1 ? 's' : ''}{' '}
                available
              </p>
            </>
          ) : (
            <>
              <input
                type="text"
                value={markerId}
                onChange={(e) => setMarkerId(e.target.value)}
                className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-text-primary focus:border-accent focus:outline-none"
                placeholder="e.g., winter-clothes"
              />
              {validating && (
                <p className="mt-1 text-xs text-text-secondary">Checking availability...</p>
              )}
              {!validating && isAvailable === false && markerId.trim() && (
                <p className="mt-1 text-xs text-red-500">This marker is already in use</p>
              )}
              {!validating && isAvailable === true && markerId.trim() && (
                <p className="mt-1 text-xs text-green-600 dark:text-green-400">
                  Marker is available
                </p>
              )}
            </>
          )}
        </div>

        {/* Marker Preview */}
        {markerId.trim() && getPreviewUrl(markerType, markerId) && (
          <div className="rounded-lg border border-border bg-surface-hover p-4">
            <h3 className="mb-2 text-sm font-medium text-text-secondary">Marker Preview</h3>
            <div className="flex items-center justify-center">
              <img
                src={getPreviewUrl(markerType, markerId)}
                alt={`${markerType} ${markerId}`}
                className="h-32 w-32 rounded border border-border"
              />
            </div>
          </div>
        )}

        {/* Box Name */}
        <div>
          <label className="mb-1 block text-sm font-medium text-text-primary">Box Name*</label>
          <input
            type="text"
            value={boxName}
            onChange={(e) => setBoxName(e.target.value)}
            className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-text-primary focus:border-accent focus:outline-none"
            placeholder="e.g., Winter Clothes"
            autoFocus
          />
        </div>

        {/* Box Location (optional) */}
        <div>
          <label className="mb-1 block text-sm font-medium text-text-primary">
            Location (optional)
          </label>
          <input
            type="text"
            value={boxLocation}
            onChange={(e) => setBoxLocation(e.target.value)}
            className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-text-primary focus:border-accent focus:outline-none"
            placeholder="e.g., Attic"
          />
        </div>

        {/* Box Notes (optional) */}
        <div>
          <label className="mb-1 block text-sm font-medium text-text-primary">
            Notes (optional)
          </label>
          <textarea
            value={boxNotes}
            onChange={(e) => setBoxNotes(e.target.value)}
            rows={3}
            className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-text-primary focus:border-accent focus:outline-none"
            placeholder="Add notes about this box..."
          />
        </div>

        {/* Actions */}
        <div className="flex gap-2 pt-2">
          <button
            onClick={onClose}
            disabled={saving}
            className="flex-1 rounded-lg border border-border bg-surface px-4 py-2 text-sm font-medium text-text-primary hover:bg-surface-hover disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={!canCreate}
            className="flex-1 rounded-lg bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? 'Creating...' : 'Create Box'}
          </button>
        </div>
      </div>
    </EditModal>
  );
}
